import React, { useState, useEffect } from 'react';
import { Card, Select, FormLayout, Popover, Button, TextField, Text, InlineStack } from '@shopify/polaris';
import { LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, LabelList } from 'recharts';
import { formatDate, formatToCurrency } from '../../../shared/components/functions';
import { useToggle } from '../../../shared/useToogle';
import { CardHeading } from '../../../shared/components/cards/CardHeading';

export default function CompanyInvoiceLines({ FIRNR }) {
  const [data, setData] = useState([]);
  const [groupBy, setGroupBy] = useState('YEAR');
  const [popoverActive, setPopoverActive] = useToggle();
  const [startDate, setStartDate] = useState(formatDate('1996-01-01'));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  const activator = (
    <Button onClick={setPopoverActive} disclosure>
      Customize
    </Button>
  );

  const options = [
    { label: 'Month', value: 'MONTH' },
    { label: 'Year', value: 'YEAR' },
    { label: 'Day', value: 'DAY' },
    { label: 'Quarter', value: 'QUARTER' }
  ];

  async function fetchData() {
    if (!FIRNR) return;
    const params = new URLSearchParams({
      groupBy: groupBy,
      startDate: startDate,
      endDate: endDate,
      companyId: FIRNR
    });
    const response = await fetch(`/api/metrics/company-invoice-lines?${params}`);
    const data = await response.json();
    setData(
      data.map((point) => {
        return {
          name: point.period,
          ...point
        };
      })
    );
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [groupBy, startDate, endDate, FIRNR]);

  const customize_graph = (
    <Popover active={popoverActive} activator={activator} onClose={setPopoverActive} ariaHaspopup={false} sectioned>
      <FormLayout>
        <Select label="Group by" options={options} onChange={setGroupBy} value={groupBy} />
        <TextField id="start_date" maxLength={60} type="date" autoComplete="off" label="Start Date" value={startDate} onChange={setStartDate} />
        <TextField id="end_date" maxLength={60} type="date" autoComplete="off" label="End Date" value={endDate} onChange={setEndDate} />
      </FormLayout>
    </Popover>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card>
          <Text className="label">{`Period : ${label}`}</Text>
          <p className="label">{`Total Invoiced : ${formatToCurrency(payload[0] ? payload[0].value : 0)}`}</p>
        </Card>
      );
    }
    return null;
  };

  return (
    <Card>
      <InlineStack align="space-between">
        <div>
          <CardHeading title={`Customer Invoice Metrics`} />
          <Text tone="subdued" as="p">
            Sum of invoice line values for the selected period VAT excluded. Canceled invoices not included.
          </Text>
        </div>
        {customize_graph}
      </InlineStack>
      <div style={{ width: '100%', height: '650px', marginTop: '20px', fontFamily: 'Inter, Arial, sans-serif' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            width={1500}
            height={300}
            data={data}
            margin={{
              top: 40,
              right: 50,
              left: 30,
              bottom: 40
            }}
          >
            <defs>
              <linearGradient id="colorInvoice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#AD0002" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#AD0002" stopOpacity={0.2} />
              </linearGradient>
              <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="6" stdDeviation="4" floodColor="#AD0002" floodOpacity="0.3" />
              </filter>
            </defs>
            <Legend />
            <CartesianGrid strokeDasharray="3 6" opacity={0.3} />
            <XAxis dataKey="period" tick={{ fontSize: 14, fontFamily: 'Inter, Arial, sans-serif' }} />
            <YAxis tick={{ fontSize: 14, fontFamily: 'Inter, Arial, sans-serif' }} />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="total_invoiced"
              stroke="url(#colorInvoice)"
              strokeWidth={4}
              dot={{ r: 8, fill: '#fff', stroke: '#AD0002', strokeWidth: 3, filter: 'url(#shadow)' }}
              activeDot={{ r: 12, fill: '#AD0002', stroke: '#fff', strokeWidth: 4, filter: 'url(#shadow)' }}
            >
              <LabelList dataKey="total_invoiced" position="top" formatter={formatToCurrency} style={{ fontWeight: 600, fill: '#AD0002', fontSize: 16, textShadow: '0 2px 8px #fff' }} />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
